import React from 'react';
import { Link } from 'react-router-dom';
import { Sun, CheckCircle, ArrowRight, Zap, Battery, Wifi, Server, ChevronRight, Phone, Network, Shield, Monitor, Lightbulb } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { SectionHeader } from '../components/common/SectionHeader';
import { CTASection } from '../components/common/CTASection';
import { GeometricBackground } from '../components/decorations/GeometricBackground';
import { services, deliveryMethodology, getRelatedServices } from '../data/services';

const iconMap = {
  Phone,
  Network,
  Server,
  Shield,
  Monitor,
  Lightbulb,
  Sun,
};

const RenewableEnergyPage = () => {
  const service = services.find(s => s.id === 'renewable-energy');
  const relatedServices = getRelatedServices('renewable-energy');

  if (!service) return null;

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-hero-pattern overflow-hidden">
        <GeometricBackground />
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <Badge className="mb-4 bg-accent/20 text-primary-foreground border-accent/30">
              Renewable Energy
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6">
              {service.name}
            </h1>
            <p className="text-lg md:text-xl text-primary-foreground/80 mb-8">
              {service.valueStatement}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild variant="hero-accent" size="xl">
                <Link to="/contact">
                  Request Consultation
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
              <Button 
                asChild 
                variant="hero-outline" 
                size="xl" 
                className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10"
              >
                <Link to="/products">
                  View Related Products
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding bg-background">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mb-6">
                <Sun className="w-8 h-8 text-accent" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                {service.detailBlock.heading}
              </h2>
              <p className="text-muted-foreground mb-8 leading-relaxed text-lg">
                {service.detailBlock.content}
              </p>

              {/* Use Cases - EXACT */}
              <h4 className="font-semibold text-foreground mb-4">Typical Use Cases:</h4>
              <ul className="space-y-3 mb-8">
                {service.useCases.map((useCase, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">{useCase}</span>
                  </li>
                ))}
              </ul>

              <Button asChild variant="accent" size="lg">
                <Link to="/contact">
                  Get a Quote
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src={service.image}
                alt="Solar panels for ICT infrastructure in Africa"
                className="rounded-2xl shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="section-padding bg-card">
        <div className="container-custom">
          <SectionHeader
            subtitle="Why Solar for ICT"
            title="Benefits of Solar-Powered Infrastructure"
            description="Sustainable, reliable power for your critical technology needs."
            className="mb-16"
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Zap, title: "Reliable Power", description: "Consistent energy supply for uninterrupted operations" },
              { icon: Battery, title: "Battery Backup", description: "Energy storage for 24/7 availability" },
              { icon: Wifi, title: "Connectivity", description: "Power for routers, radios, and network equipment" },
              { icon: Server, title: "Data Centers", description: "Sustainable power for computer labs and servers" },
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 rounded-xl bg-accent/10 flex items-center justify-center mx-auto mb-4">
                      <feature.icon className="w-7 h-7 text-accent" />
                    </div>
                    <h3 className="font-semibold text-foreground mb-2">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How We Deliver Section */}
      <section className="section-padding bg-background">
        <div className="container-custom">
          <SectionHeader
            subtitle="Our Approach"
            title="How We Deliver Solar Solutions"
            description="A proven methodology that ensures successful project outcomes."
            className="mb-16"
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {deliveryMethodology.map((phase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="w-10 h-10 rounded-full bg-accent text-accent-foreground flex items-center justify-center mb-4 font-bold">
                      {index + 1}
                    </div>
                    <h3 className="font-semibold text-foreground mb-2">{phase.step}</h3>
                    <p className="text-sm text-muted-foreground">{phase.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline/Process (Original) */}
      <section className="section-padding bg-card">
        <div className="container-custom">
          <SectionHeader
            subtitle="Project Timeline"
            title="Solar Deployment Process"
            description="A structured approach specifically designed for solar ICT projects."
            className="mb-16"
          />

          <div className="max-w-4xl mx-auto">
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-accent/20 -translate-x-1/2" />

              {service.timeline.map((step, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className={`relative flex items-center gap-6 mb-8 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                >
                  {/* Step number */}
                  <div className="absolute left-4 md:left-1/2 w-8 h-8 rounded-full bg-accent text-accent-foreground flex items-center justify-center font-bold text-sm -translate-x-1/2 z-10">
                    {i + 1}
                  </div>

                  {/* Content */}
                  <div className={`flex-1 ml-16 md:ml-0 ${i % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12 md:text-left'}`}>
                    <Card className="inline-block">
                      <CardContent className="p-6">
                        <h4 className="font-semibold text-foreground mb-2">{step.step}</h4>
                        <p className="text-sm text-muted-foreground">{step.description}</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Spacer for alternating layout */}
                  <div className="hidden md:block flex-1" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Related Services Section */}
      <section className="section-padding bg-background">
        <div className="container-custom">
          <SectionHeader
            subtitle="Explore More"
            title="Related Services"
            description="Discover other solutions that complement renewable energy deployments."
            className="mb-12"
          />

          <div className="grid md:grid-cols-3 gap-6">
            {relatedServices.map((related, index) => {
              const RelatedIcon = iconMap[related.icon] || Server;
              return (
                <motion.div
                  key={related.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link to={related.route}>
                    <Card className="h-full hover:shadow-xl hover:border-primary/30 hover:-translate-y-1 transition-all cursor-pointer">
                      <CardContent className="p-6">
                        <div className={`w-12 h-12 rounded-xl ${related.color === 'accent' ? 'bg-accent/10' : 'bg-primary/10'} flex items-center justify-center mb-4`}>
                          <RelatedIcon className={`w-6 h-6 ${related.color === 'accent' ? 'text-accent' : 'text-primary'}`} />
                        </div>
                        <h3 className="font-semibold text-foreground mb-2">{related.name}</h3>
                        <p className="text-sm text-muted-foreground mb-4">{related.shortDescription}</p>
                        <span className={`inline-flex items-center text-sm font-medium ${related.color === 'accent' ? 'text-accent' : 'text-primary'}`}>
                          Learn More
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </span>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTASection
        title="Power Your ICT with Solar"
        description="Let's discuss how solar energy can provide reliable, sustainable power for your technology infrastructure."
        primaryButtonText="Contact Us"
        primaryButtonLink="/contact"
        variant="accent"
      />
    </div>
  );
};

export default RenewableEnergyPage;
